import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { money } from "@/lib/locale";
import { Link } from "react-router-dom";
import { Wallet, Flame, Shield, Car, Info } from "lucide-react";

export default function BudgetTile() {
  const [b, setB] = useState(null);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/budget/snapshot");
      setB(data);
    } catch (err) {
      console.error("Failed to load budget snapshot:", err);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!b) return null;
  const total = b.monthly_total;
  const breakdown = [
    { key: "monthly_bills",     label: "Bills",     value: b.monthly_bills,     icon: Flame,  tone: "text-hearth-clay" },
    { key: "monthly_insurance", label: "Insurance", value: b.monthly_insurance, icon: Shield, tone: "text-hearth-ink" },
    { key: "monthly_vehicle",   label: "Vehicle",   value: b.monthly_vehicle,   icon: Car,    tone: "text-hearth-slate" },
  ];
  const max = Math.max(0.01, ...breakdown.map((x) => x.value));

  return (
    <section className="warm-card p-6 fade-up" data-testid="budget-tile">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 caption"><Wallet size={13} /> Monthly snapshot</div>
        <button onClick={() => setOpen((v) => !v)} className="caption text-hearth-mute hover:text-hearth-ink inline-flex items-center gap-1">
          <Info size={12} /> {open ? "hide" : "how?"}
        </button>
      </div>
      <div className="flex items-baseline gap-3">
        <div className="stat-num text-hearth-ink num">{money(total)}</div>
        <div className="text-sm text-hearth-mute">
          / month <span className="num text-hearth-ink">{money(b.annual_total, { withCents: false })}</span> a year
        </div>
      </div>

      <div className="mt-5 space-y-3">
        {breakdown.map((row) => {
          const pct = (row.value / max) * 100;
          const Icon = row.icon;
          return (
            <div key={row.key}>
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="inline-flex items-center gap-2 text-hearth-ink">
                  <Icon size={13} className={row.tone} /> {row.label}
                </span>
                <span className="font-mono text-hearth-ink">{money(row.value)}</span>
              </div>
              <div className="h-1.5 bg-hearth-stone rounded-full overflow-hidden">
                <div
                  className={`h-full ${row.key === "monthly_bills" ? "bg-hearth-clay" : row.key === "monthly_insurance" ? "bg-hearth-ink" : "bg-hearth-slate"}`}
                  style={{ width: `${pct}%`, transition: "width 0.6s ease" }}
                />
              </div>
            </div>
          );
        })}
      </div>

      {Object.keys(b.by_category).length > 0 && (
        <div className="mt-5 pt-4 border-t border-hearth-line/70">
          <div className="caption mb-2">Bills by category</div>
          <div className="flex flex-wrap gap-1.5">
            {Object.entries(b.by_category).map(([k, v]) => (
              <span key={k} className="chip bg-hearth-stone text-hearth-ink">
                {k} <span className="font-mono text-hearth-mute ml-1">{money(v, { withCents: false })}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {open && (
        <div className="mt-4 p-4 rounded-lg bg-hearth-stone/60 text-xs text-hearth-mute leading-relaxed space-y-1">
          {b.notes.map((n) => <p key={n}>• {n}</p>)}
          <p className="pt-1">
            Manage values: <Link to="/bills" className="text-hearth-clay underline">Bills</Link>, <Link to="/insurance" className="text-hearth-clay underline">Insurance</Link>, <Link to="/vehicles" className="text-hearth-clay underline">Vehicles</Link>.
          </p>
        </div>
      )}
    </section>
  );
}
